// useOnboardingGate.js -- owns onboarding state for the mounted app (spec §4). Single job: gather the
// inputs, resolve the step, run the side effects (attach, outbox flush, analytics pause/resume, the
// pre-consent crash beacon listener, locks).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { supabase } from '../../lib/supabase';
import { signOutSlipTrack } from '../../lib/signOutSlipTrack';
import { initAnalytics, identifyUser, track, setAnalyticsAllowed, releaseSignIn, dropSignIn } from '../../lib/analytics';
import { isCapacitorNative, openInAppBrowser, reloadApp, getOrigin, addGlobalErrorListener, addStorageListener } from '../../lib/platform';
import { PUBLIC_WEB_BASE } from '../../lib/api';
import * as Sentry from '../../lib/errorReporting';
import { reportSentryOnly } from '../../lib/errorReporting';
import { BUNDLED_LEGAL_VERSIONS, fetchLegalVersions } from './legalVersions';
import { readDevice, writeDevice, updateDevice, syncFromStorage, DEVICE_KEY, DEVICE_BLOCKS_KEY } from './deviceRecords';
import { fetchLatestConsents, buildRows } from './accountRecords';
import { enqueueRows, flushOutbox } from './consentOutbox';
import { sendCrash } from './crashBeacon';
import { resolveOnboardingStep, ANALYTICS_STEPS, FRESH_MS, LOCK_MS } from './resolveOnboardingStep';
import { isAtLeast21, todayLocal } from './age';
import { authSubject, addAuthSubjectListener } from '../../lib/authSubject';

const STALL_MS = 10000;
const iso = () => new Date().toISOString();
const capAtNow = (at) => new Date(Math.min(Date.parse(at), Date.now())).toISOString();
// Ids of this user's outbox entries still awaiting a send (parked entries are settled).
const pendingIds = (id) => readDevice().queue.filter((q) => q.userId === id && !q.parked).map((q) => q.id);
// Merge rows enqueued locally into `latest` (spec §4 step 4.4). An age under_21 row is also the newest
// under_21 row, which rule 4 reads. `at` stands in for created_at: a pending row keeps its queued time, so
// a later read does not move its 24h block forward (#1444 Nova note).
function mergeRows(latest, rows, at = iso()) {
  const out = { ...latest };
  for (const r of rows) {
    const rec = { kind: r.kind, version: r.version, result: r.result, attested_at: r.attested_at, created_at: at };
    out[r.kind] = rec;
    if (r.kind === 'age' && r.result === 'under_21') out.under21 = rec;
  }
  return out;
}

export function useOnboardingGate({ user, authResolved, showAuth }) {
  const [device, setDevice] = useState(() => readDevice());
  // D7 order for the first frames too: the last good remote copy, else bundled (a legal-only deploy must
  // not show an up-to-date user the Terms screen, or record an old version, while the fetch runs).
  const [versions, setVersions] = useState(() => readDevice().lastVersions || BUNDLED_LEGAL_VERSIONS);
  const versionsRef = useRef(versions);
  versionsRef.current = versions;
  const [account, setAccount] = useState({ uid: null, status: 'idle', latest: null });
  const accountRef = useRef(account);
  accountRef.current = account;
  const [now, setNow] = useState(() => Date.now());
  const [stalled, setStalled] = useState(false);
  const uid = user?.id || null;
  const uidRef = useRef(uid);
  uidRef.current = uid;
  const prevUid = useRef(undefined);
  const reqId = useRef(0);
  // Account reads (normal or silent) in flight, per uid, counted synchronously (state and accountRef lag
  // a render). A foreground re-read never starts over one (pre-Codex FC I2; #1446 F1). App entry, the
  // analytics resume, the sign-in release and the cached-pass write wait for ALL of this user's reads,
  // not just the latest (#1458 P1-A: a superseded read can still carry a block). A read for another uid
  // never holds this one (K4). The count stays up through a stale read's replacement read.
  const inFlight = useRef(new Map());
  const [, setReadTick] = useState(0);
  // Bumped by every local acceptance (persist). A silent re-read that started before one is a stale
  // snapshot and is dropped (Nova P2-1: it would replace the merged rows and re-ask the user), except
  // for an active under_21, which is applied (#1444).
  const localWrites = useRef(0);
  const loadAccountRef = useRef(null);
  const analyticsInit = useRef(false);
  const identified = useRef(null);
  const lastTracked = useRef(null);
  const analyticsOn = useRef(false);
  const stepRef = useRef('loading');

  const save = useCallback((patch) => { const next = writeDevice(patch); setDevice(next); setNow(Date.now()); return next; }, []);
  // #1464: record that account `id` was seen blocked, at `at` (capped at now; the later record wins). Keyed by
  // account, so it is written for ANY id -- current, superseded, signed out or switched away -- and the
  // resolver blocks that account ahead of its account state and cached pass. Monotonic: nothing unwrites it
  // before it expires (deviceRecords prunes entries 24h old).
  const recordBlock = useCallback((id, at) => {
    if (!id || !at || Number.isNaN(Date.parse(at))) return;
    const capped = capAtNow(at);
    const next = updateDevice((d) => {
      const prev = Object.prototype.hasOwnProperty.call(d.blocks, id) ? d.blocks[id] : null;
      return prev && Date.parse(prev) >= Date.parse(capped) ? {} : { blocks: { ...d.blocks, [id]: capped } };
    });
    setDevice(next); setNow(Date.now());
  }, []);
  // A device-wide write that runs when an async read or send settles acts for `id` only if it is current
  // by both authorities: the rendered uid, and the auth source's subject (published synchronously in its
  // callbacks, so it can run a render ahead; undefined when no auth source ever published, e.g. tests
  // without useAuth). #1461 P1-D.
  const isCurrentBoth = useCallback((id) => {
    const sub = authSubject();
    return id === uidRef.current && (sub === undefined || sub === id);
  }, []);
  // 6(b): stamp the phone lock for `id` from its newest active evidence (the account's under_21 or its block
  // record), only while `id` is current by both authorities, and only if that moves the lock forward.
  const stampLockFor = useCallback((id, acct, dev) => {
    if (!id || !isCurrentBoth(id)) return;
    const u21 = acct.uid === id ? acct.latest?.under21 : null;
    const rec = Object.prototype.hasOwnProperty.call(dev.blocks, id) ? dev.blocks[id] : null;
    // Active evidence only (< 24h): the auth-boundary listener runs on every auth change, not just on a blocked
    // render, and an expired under_21 must not stamp a lock or clear the phone's age pass (#1469).
    const t = Date.now();
    const active = (iso) => iso && t - Math.min(Date.parse(iso), t) < LOCK_MS;
    const sources = [active(u21?.created_at) && capAtNow(u21.created_at), active(rec) && capAtNow(rec)].filter(Boolean);
    if (!sources.length) return;
    const at = sources.reduce((a, b) => (Date.parse(a) >= Date.parse(b) ? a : b));
    if (dev.lock && Date.parse(dev.lock.at) >= Date.parse(at)) return;
    save({ lock: { at }, age: null }); // as on the phone path: no stale pass left for guest mode
  }, [save, isCurrentBoth]);
  const startRead = useCallback((id) => { inFlight.current.set(id, (inFlight.current.get(id) || 0) + 1); setReadTick((n) => n + 1); }, []);
  const endRead = useCallback((id) => {
    const n = (inFlight.current.get(id) || 0) - 1;
    if (n > 0) inFlight.current.set(id, n); else inFlight.current.delete(id);
    setReadTick((x) => x + 1);
  }, []);
  // An active under_21 seen by any read is applied, whether that read is current, stale, superseded
  // (#1458 P1-A), failed partway (#1458 P1-B) or fully successful (#1461 Ultron P1-1). It is merged into
  // that uid's `ok` account state (rule 4; keyed by uid, never another user's). The device-wide phone
  // lock (rule 1) is stamped at its created_at (capped at now, only if newer) only when the read's user is
  // the current one by BOTH authorities: the rendered uid and the auth source's published subject, which
  // runs ahead of the render (#1461 P1-D: a read that settles after auth moved to another user, or signed
  // out, before React rendered it, must not lock that next user). Otherwise it touches nothing device-wide
  // except that account's own cached pass, cleared so it cannot admit the account later over a failed
  // read (#1458 Ultron #1).
  const applyBlock = useCallback((id, u21) => {
    if (!u21) return;
    const t = Date.now();
    if (!(t - Math.min(Date.parse(u21.created_at), t) < LOCK_MS)) return; // expired (or unparseable)
    recordBlock(id, u21.created_at); // #1464: the authority, written first and for any id
    setAccount((a) => (a.uid === id && a.status === 'ok' && !(a.latest?.under21 && Date.parse(a.latest.under21.created_at) >= Date.parse(u21.created_at))
      ? { ...a, latest: { ...a.latest, under21: u21 } } : a));
    if (!isCurrentBoth(id)) {
      if (readDevice().accountPass?.userId === id) save({ accountPass: null });
      return;
    }
    const at = capAtNow(u21.created_at);
    const d = readDevice();
    if (!d.lock || Date.parse(d.lock.at) < Date.parse(at)) save({ lock: { at }, age: null });
  }, [save, isCurrentBoth, recordBlock]);
  const refresh = useCallback(() => { setDevice(readDevice()); setNow(Date.now()); }, []);
  // A send the server's 24h guard rejected (ST021) refetches the account: the under_21 row then
  // resolves the gate to `blocked` and the lock effect stamps the phone (PR 1 amendment c).
  // Resolves { reloaded }: whether it started an account reload.
  const flush = useCallback(async (id) => {
    if (!id) return { reloaded: false };
    const { blocked, parked } = await flushOutbox(supabase, id, { now: Date.now(), onBlocked: (u) => recordBlock(u, iso()) });
    refresh();
    // #1464: ST021 proves an active under_21 for `id` now; record it for that account whatever its authority.
    if (blocked) recordBlock(id, iso());
    // ST021 proves an active under_21 for `id`. When `id` is no longer current (rendered away, or auth
    // already moved on), revoke its own cached pass here: a re-read now runs under the next session and
    // cannot see its rows (pre-Codex FC F1 / Sec F1; #1458 Ultron #1). Nothing else device-wide (K4).
    if (blocked && !isCurrentBoth(id) && readDevice().accountPass?.userId === id) save({ accountPass: null });
    if (id !== uidRef.current) return { reloaded: false };
    // A parked (terminal) reject: rows merged into `latest` were never stored, so reload the account and
    // the user is asked again (spec §10).
    if (parked && !blocked) { if (loadAccountRef.current) loadAccountRef.current(id); return { reloaded: true }; }
    if (!blocked) return { reloaded: false };
    // Auth has already moved off this user (#1461 P1-D): no device-wide lock. Re-read instead; the read's
    // under_21 then applies through applyBlock, which locks only for the user current by both authorities.
    if (!isCurrentBoth(id)) { if (loadAccountRef.current) loadAccountRef.current(id); return { reloaded: true }; }
    // ST021 proves the server's 24h window is active now: lock the phone at now so a skewed clock cannot
    // read the refetched under_21 row as expired and re-ask for age (spec §6).
    const d = readDevice();
    if (!d.lock || Date.now() - Math.min(Date.parse(d.lock.at), Date.now()) >= LOCK_MS) { setDevice(writeDevice({ lock: { at: iso() }, age: null })); setNow(Date.now()); }
    if (loadAccountRef.current) loadAccountRef.current(id);
    return { reloaded: true };
  }, [refresh, isCurrentBoth, save, recordBlock]);

  // #1469 (GPT #1465 P1): when auth leaves the account this gate shows, stamp its phone lock from its active
  // evidence synchronously, at the auth boundary, before the subject changes (stampLockFor's two-authority
  // check still sees it as current). An external SIGNED_OUT can otherwise land between a blocked commit and
  // the passive 6(b) effect and reach guest mode unlocked.
  useEffect(() => addAuthSubjectListener((prev, next) => {
    if (!prev || prev === next || prev !== uidRef.current) return;
    try {
      stampLockFor(prev, accountRef.current, readDevice());
    } catch (err) {
      // Terminal for this sign-out: once the subject changes, no later path can stamp for `prev` (CC-IMPL-037).
      Sentry.captureException(err, { tags: { function: 'useOnboardingGate', stage: 'auth-boundary-lock' } });
    }
  }), [stampLockFor]);

  // #1464 Codex r1 P1: another web tab that records a block (or stamps the lock) for an account open here
  // reaches this mounted gate at once: merge it into this tab's memory and re-render (rule 2a / rule 1).
  // Subscribe, then sync once at once (Codex r2): a write another tab made before this listener existed --
  // while this tab's memory was already set, or while the gate was unmounted -- fires no event here.
  useEffect(() => {
    const sync = () => { setDevice(syncFromStorage()); setNow(Date.now()); };
    const removers = typeof addStorageListener === 'function' ? [addStorageListener(DEVICE_KEY, sync), addStorageListener(DEVICE_BLOCKS_KEY, sync)] : [];
    sync();
    return () => removers.forEach((r) => r && r());
  }, []);

  useEffect(() => {
    let live = true;
    fetchLegalVersions({ lastGood: readDevice().lastVersions }).then(({ versions: v, source }) => {
      if (!live) return;
      if (source === 'remote') save({ lastVersions: v });
      setVersions(v);
    });
    return () => { live = false; };
  }, [save]);

  // silent (#1438 F3, the foreground re-read): no `loading` frame, and a failed read keeps the current
  // state instead of showing the account error. The request-token guard is the same.
  const loadAccount = useCallback(async (id, { silent = false } = {}) => {
    const token = ++reqId.current;
    const writes = localWrites.current;
    const pendingAtStart = pendingIds(id);
    startRead(id); // before the first await: a caller ending its own read right after this keeps the count up
    if (!silent) setAccount({ uid: id, status: 'loading', latest: null });
    let latest;
    try {
      latest = await fetchLatestConsents(supabase, id);
    } catch (err) {
      applyBlock(id, err?.under21); // #1458 P1-B, current or superseded
      endRead(id);
      if (token !== reqId.current) return;
      if (silent) {
        Sentry.captureMessage('onboarding: account re-read failed on foreground', { level: 'warning', tags: { function: 'useOnboardingGate', stage: 'foreground' } });
        return;
      }
      Sentry.captureMessage('onboarding: account records unavailable', { level: 'warning', tags: { function: 'useOnboardingGate', stage: 'load' } });
      setAccount({ uid: id, status: 'error', latest: null });
      return;
    }
    if (token !== reqId.current) { applyBlock(id, latest.under21); endRead(id); return; } // #1458 P1-A
    const t = Date.now();
    // Rule 4 (PR 1b): the newest under_21 row, not the latest age row.
    const recentUnder21 = latest.under21 && t - Math.min(Date.parse(latest.under21.created_at), t) < LOCK_MS;
    // Stale snapshot: a local acceptance landed while this read ran (#1444), or an outbox entry pending at
    // its start was sent or dropped meanwhile, so the server may now hold rows the snapshot lacks (#1446 F2).
    // Gone = removed from the queue (sent, or dropped by ST021); a parked entry is still there (it reloads).
    const remaining = new Set(readDevice().queue.filter((q) => q.userId === id).map((q) => q.id));
    const delivered = pendingAtStart.some((q) => !remaining.has(q));
    if ((silent && writes !== localWrites.current) || delivered) {
      // Its legal rows are not applied (P2-1), but an active under_21 is authoritative: apply it, lock
      // included whatever the account status, so rule 1 blocks even if the re-read fails (#1446 pre-Codex
      // FC/Sec P2). Then re-read once the flush settles; this read's count is released only after the
      // replacement (or the flush's own reload) has started its read.
      if (recentUnder21) applyBlock(id, latest.under21);
      flush(id).then(({ reloaded }) => {
        if (reloaded || id !== uidRef.current || token !== reqId.current) return;
        loadAccountRef.current(id, { silent });
      }).finally(() => endRead(id));
      return;
    }
    endRead(id);
    // #1461 Ultron P1-1: a full-success read's block goes through applyBlock too, so it reaches this user's
    // cached pass when auth has already moved away (for the current user it stamps the lock the blocked
    // step's effect would stamp a render later).
    if (recentUnder21) applyBlock(id, latest.under21);
    if (!recentUnder21) {
      const d = readDevice();
      const fresh = (rec) => rec && !rec.attachedTo && t - Date.parse(rec.attestedAt) <= FRESH_MS;
      const needAge = latest.age?.result !== 'passed' && fresh(d.age);
      // Skip only what the account already satisfies at the CURRENT versions; attach a phone acceptance
      // only if it is for the current versions.
      const v = versionsRef.current;
      const accountLegal = latest.terms?.version === v.terms && latest.privacy?.version === v.privacy;
      const phoneLegal = fresh(d.legal) && d.legal.terms === v.terms && d.legal.privacy === v.privacy;
      const needLegal = !accountLegal && phoneLegal;
      // Attaching moves the phone's records into this user's outbox (device-wide): only for the user
      // current by both authorities, never for one auth has already left (pre-Codex Sec F2).
      // ...and never for an account with an active block record (#1464, Ultron P3-5): it is blocked.
      if ((needAge || needLegal) && isCurrentBoth(id) && !Object.prototype.hasOwnProperty.call(readDevice().blocks, id)) {
        // Separate entries: an ST021 on the age row must not drop the Terms rows with it.
        const ageRows = needAge ? buildRows({ userId: id, age: d.age, legal: null }) : [];
        const legalRows = needLegal ? buildRows({ userId: id, age: null, legal: d.legal }) : [];
        if (ageRows.length) enqueueRows(id, ageRows, t);
        if (legalRows.length) enqueueRows(id, legalRows, t);
        save({ age: needAge ? { ...d.age, attachedTo: id } : d.age, legal: needLegal ? { ...d.legal, attachedTo: id } : d.legal });
      }
      // This user's rows still pending in the outbox (attached just above, or accepted earlier) are merged
      // as persist merges them: a read that lands before they drain must not drop them, or the user is
      // asked again once they are sent (pre-Codex FC F1). Parked rows are left out (spec §10: asked again).
      for (const q of readDevice().queue) if (q.userId === id && !q.parked) latest = mergeRows(latest, q.rows, q.queuedAt);
    }
    setAccount({ uid: id, status: 'ok', latest });
    flush(id);
  }, [save, flush, startRead, endRead, applyBlock, isCurrentBoth]);
  loadAccountRef.current = loadAccount;

  useEffect(() => {
    if (uid === prevUid.current) return; // CC-IMPL-021
    prevUid.current = uid;
    if (uid) loadAccount(uid); else { reqId.current += 1; dropSignIn(); setAccount({ uid: null, status: 'idle', latest: null }); }
  }, [uid, loadAccount]);

  // #1438 F3: returning to the foreground flushes, then re-reads the account silently, so an under_21
  // row written from another device blocks the open app at the next foreground (spec §4 6(e)). Skipped
  // while a read (normal or silent) is already running for this user (#1446 F1), and when the flush
  // already reloaded.
  const onForeground = useCallback(async (id) => {
    const { reloaded } = await flush(id);
    const a = accountRef.current;
    if (reloaded || id !== uidRef.current || inFlight.current.has(id) || a.uid !== id || (a.status !== 'ok' && a.status !== 'error')) return;
    loadAccount(id, { silent: true });
  }, [flush, loadAccount]);

  useEffect(() => {
    if (!uid || !isCapacitorNative()) return undefined;
    let handle = null; let cancelled = false;
    (async () => {
      try {
        const h = await App.addListener('appStateChange', ({ isActive }) => { if (isActive) onForeground(uid); });
        if (cancelled) { h.remove(); return; }
        handle = h;
      } catch {
        Sentry.captureMessage('onboarding: appStateChange listener unavailable', { level: 'warning', tags: { function: 'useOnboardingGate' } });
      }
    })();
    return () => { cancelled = true; if (handle) handle.remove(); };
  }, [uid, onForeground]);

  const input = { now, authResolved, user: user ? { id: user.id, created_at: user.created_at } : null, showAuth, versions, device, account };
  const step = resolveOnboardingStep(input);
  const reading = Boolean(uid) && inFlight.current.has(uid);
  // Paused analytics does not resume while an account read for this user is in flight: it can still
  // return a block (#1444 pre-Codex Sec P2). An already-allowed session is left running.
  const allowed = ANALYTICS_STEPS.has(step) && !(reading && !analyticsOn.current);
  // What the gate shows: entering the app (from Terms or age) while a read for this user is in flight
  // shows `loading` until it settles, since it can still return a block (#1446 Codex r1 P1). An app
  // already open (analytics running) stays shown during a foreground read: no loading frame (6(e)).
  const shownStep = step === 'app' && !allowed ? 'loading' : step;
  stepRef.current = step;

  // D9: initialize once at an analytics step; pause whenever the step leaves that set.
  useEffect(() => {
    analyticsOn.current = allowed;
    if (!uid) identified.current = null; // useAuth's resetUser cleared the identity; re-identify on sign-in
    if (allowed) {
      if (!analyticsInit.current) { analyticsInit.current = true; initAnalytics({ isNative: isCapacitorNative, onInternalError: reportSentryOnly }); }
      setAnalyticsAllowed(true);
      if (uid && identified.current !== uid) { identified.current = uid; identifyUser(uid); }
    } else if (analyticsInit.current) {
      setAnalyticsAllowed(false);
    }
  }, [allowed, uid]);

  // #1438 F1: useAuth holds sign_in_completed for the incoming uid; it is captured only once that uid's
  // gate reaches `app` on a successful account read (declared after the D9 effect, so analytics has
  // resumed), and dropped on a block, an account error or a sign-out (the uid effect above). A cached
  // pass over a failed read reaches `app` but keeps it held (pre-Codex Sec I1). It also waits until none
  // of this user's rows are still pending in the outbox (Codex r1 P1): rows merged locally can reach
  // `app` before the server rejects them (ST021 -> blocked, terminal -> asked again). A different uid's
  // sign-in replaces it (analytics.holdSignIn). And it waits while an account read for this uid is in
  // flight (#1444): a foreground read can still return an under_21 from another device.
  const pendingRows = device.queue.some((q) => q.userId === uid && !q.parked);
  useEffect(() => {
    if (!uid || account.uid !== uid) return;
    if (step === 'app' && account.status === 'ok' && !pendingRows && !reading) releaseSignIn(uid);
    else if (step === 'blocked' || step === 'account_error') dropSignIn();
  }, [step, uid, account.uid, account.status, pendingRows, reading]);

  // D9: before consent, global errors go to the anonymous crash beacon (the SDK is off or paused).
  useEffect(() => {
    if (allowed) return undefined;
    return addGlobalErrorListener((err) => { sendCrash(err, { step: stepRef.current, isNative: isCapacitorNative }); });
  }, [allowed]);

  useEffect(() => {
    if (!allowed || lastTracked.current === step) return;
    lastTracked.current = step;
    track('onboarding_step_viewed', { step });
  }, [allowed, step]);

  // §4 6(b): an account block stamps the phone at the newest under_21 row's created_at (capped at now),
  // so signing out cannot reach guest mode. An older (expired) phone lock is re-stamped. Only for the user
  // current by both authorities (#1461 Codex r1 P1): a block merged into A's state can render while auth
  // has already moved to B; the lock is device-wide and must not land on B. A's evidence stays merged.
  // #1464: the source is the account's newest under_21 or this account's block record, whichever is newer.
  // It runs after EVERY render (no dependency list; it is idempotent): the auth subject is not React state,
  // so a subject that returns to A with no state change must still stamp at A's next blocked render
  // (pre-Codex FC F1). onSignOut stamps it too, before signing out.
  useEffect(() => {
    if (step !== 'blocked') return;
    stampLockFor(uid, account, device);
  });

  useEffect(() => {
    if (device.lock && Date.parse(device.lock.at) > Date.now()) save({ lock: { at: iso() } });
  }, [device.lock, save]);

  // §4 6(c): re-check at expiry, capped at 1h. `now` is a dependency so each capped tick re-arms the
  // timer until the block lifts (without it a block more than 1h from expiry never lifts in-app).
  useEffect(() => {
    if (step !== 'blocked') return undefined;
    const u21 = account.latest?.under21;
    const rec = uid && Object.prototype.hasOwnProperty.call(device.blocks, uid) ? device.blocks[uid] : null; // #1464
    const ends = [device.lock && Date.parse(device.lock.at) + LOCK_MS, u21 && Date.parse(u21.created_at) + LOCK_MS, rec && Date.parse(rec) + LOCK_MS].filter(Boolean);
    const wait = Math.min(Math.max(0, Math.max(...ends) - Date.now()) + 1000, 3600000);
    const id = setTimeout(() => setNow(Date.now()), wait);
    return () => clearTimeout(id);
  }, [step, uid, device.lock, device.blocks, account.latest, now]);

  useEffect(() => {
    if (shownStep !== 'loading') { setStalled(false); return undefined; }
    const id = setTimeout(() => setStalled(true), STALL_MS);
    return () => clearTimeout(id);
  }, [shownStep]);

  useEffect(() => {
    // Not while any read for this user is in flight: one may still carry a block (#1458 Ultron #3).
    if (step !== 'app' || !uid || account.uid !== uid || account.status !== 'ok' || reading) return;
    // The cached pass stands in for account rows when a later read fails, so only write it once this
    // user's rows are on the server (nothing of theirs left in the outbox).
    if (device.queue.some((q) => q.userId === uid)) return;
    const p = device.accountPass;
    if (p && p.userId === uid && p.terms === versions.terms && p.privacy === versions.privacy) return;
    save({ accountPass: { userId: uid, terms: versions.terms, privacy: versions.privacy } });
  }, [step, uid, account.uid, account.status, reading, versions, device.accountPass, device.queue, save]);

  const persist = useCallback((rows) => {
    localWrites.current += 1;
    enqueueRows(uid, rows, Date.now());
    setAccount((a) => (a.uid === uid && a.status === 'ok' ? { ...a, latest: mergeRows(a.latest, rows) } : a));
    refresh();
    flush(uid);
  }, [uid, refresh, flush]);

  const handlers = useMemo(() => ({
    onWelcomeContinue: () => save({ welcomeSeen: true }),
    onAgeConfirm: (dob) => {
      const at = iso();
      if (isAtLeast21(dob, todayLocal())) {
        const rec = { result: 'passed', version: versions.age, attestedAt: at, attachedTo: uid };
        save({ age: rec });
        if (uid) persist(buildRows({ userId: uid, age: rec, legal: null }));
      } else {
        save({ lock: { at }, age: null });
        if (uid) persist(buildRows({ userId: uid, age: { result: 'under_21', version: versions.age, attestedAt: at }, legal: null }));
      }
    },
    onAgree: () => {
      const rec = { terms: versions.terms, privacy: versions.privacy, attestedAt: iso(), attachedTo: uid };
      save({ legal: rec });
      if (uid) persist(buildRows({ userId: uid, age: null, legal: rec }));
    },
    onSignOut: async () => {
      // 6(b) before leaving: a blocked account cannot sign out into guest mode (pre-Codex FC F1).
      if (stepRef.current === 'blocked') stampLockFor(uid, accountRef.current, readDevice());
      // #1415 D6 (Codex P1 on #1476): through signOutSlipTrack, so the sportsbook session ends with this one.
      try { await signOutSlipTrack(); } catch (err) { Sentry.captureException(err, { tags: { function: 'useOnboardingGate', stage: 'signout' } }); }
    },
    onRetry: () => { if (uid) loadAccount(uid); },
    onReload: () => reloadApp(getOrigin()),
    onOpenLegal: (file) => openInAppBrowser(`${PUBLIC_WEB_BASE}/${file}`),
  }), [save, versions, uid, persist, loadAccount, stampLockFor]);

  return { step: shownStep, stalled, versions, allowed, handlers };
}
